import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './AssistantConversations.css';

const LIMIT = 50;

const formatRelative = (value) => {
  if (!value) return 'never';
  const t = new Date(value).getTime();
  if (Number.isNaN(t)) return 'unknown';
  const diff = Date.now() - t;
  if (diff < 0) return 'just now';
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.floor(mo / 12)}y ago`;
};

const formatAbsolute = (value) => {
  if (!value) return 'N/A';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'N/A';
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const formatUserName = (row) => {
  const parts = [row.userName, row.userSurname].filter(Boolean);
  return parts.length ? parts.join(' ') : 'Anonymous';
};

const AssistantConversations = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const { fetchWithAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const userIdFilter = searchParams.get('userId');
  const userFilterRef = useRef(null);
  const [userFilter, setUserFilter] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    setPage(1);
  }, [search, userIdFilter]);

  const fetchConversations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(LIMIT));
      if (search) params.set('search', search);
      if (userIdFilter) params.set('userId', userIdFilter);
      const response = await fetchWithAuth(`/api/admin/assistant/conversations?${params.toString()}`);
      if (!response.ok) throw new Error('Failed to load conversations');
      const data = await response.json();
      setItems(data.data || []);
      setTotal(data.total || 0);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, page, search, userIdFilter]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  useEffect(() => {
    if (!userIdFilter) {
      setUserFilter(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetchWithAuth(`/api/admin/assistant/users?limit=200`);
        if (!resp.ok) return;
        const data = await resp.json();
        const found = (data.data || []).find((u) => String(u.id) === String(userIdFilter));
        if (!cancelled && found) setUserFilter(found);
      } catch (_) { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [fetchWithAuth, userIdFilter]);

  const clearUserFilter = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('userId');
    setSearchParams(next);
    setUserFilter(null);
  };

  const clearAllFilters = () => {
    setSearchInput('');
    setSearch('');
    clearUserFilter();
  };

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <div>
      <h1 style={{ fontSize: '32px', fontWeight: '700', marginBottom: '24px' }}>AI Chats</h1>

      <div className="card">
        <div className="assistant-filter-bar">
          <input
            ref={userFilterRef}
            type="text"
            placeholder="Search by title, name, or email..."
            className="input"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={{ maxWidth: 360 }}
          />
          {userFilter && (
            <span className="assistant-chip">
              User: {formatUserName(userFilter)} ({userFilter.email})
              <button onClick={clearUserFilter} className="assistant-chip-x" aria-label="Clear user filter">×</button>
            </span>
          )}
          {(search || userIdFilter) && (
            <button className="btn btn-secondary" onClick={clearAllFilters}>Clear filters</button>
          )}
          <span style={{ color: '#666', fontSize: 14, marginLeft: 'auto' }}>
            {total.toLocaleString()} conversations
          </span>
        </div>

        {error && <div className="error" style={{ padding: 12, color: '#b91c1c' }}>Error: {error}</div>}

        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>User</th>
                <th>Title</th>
                <th style={{ width: 100 }}>Messages</th>
                <th style={{ width: 160 }}>Last message</th>
                <th style={{ width: 160 }}>Created</th>
              </tr>
            </thead>
            <tbody>
              {loading && items.length === 0 ? (
                <tr><td colSpan={5} style={{ padding: 24, color: '#666' }}>Loading...</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={5} style={{ padding: 24, color: '#666' }}>
                  No conversations {search || userIdFilter ? 'match these filters' : 'yet'}.
                </td></tr>
              ) : (
                items.map((c) => (
                  <tr
                    key={c.id}
                    className="assistant-row-clickable"
                    onClick={() => navigate(`/assistant/conversations/${c.id}`)}
                  >
                    <td>
                      <div style={{ fontWeight: 500 }}>{formatUserName(c)}</div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>{c.userEmail || `user #${c.userId}`}</div>
                    </td>
                    <td>
                      {c.title ? (
                        <span>{c.title}</span>
                      ) : (
                        <span style={{ fontStyle: 'italic', color: '#9ca3af' }}>&lt;untitled&gt;</span>
                      )}
                    </td>
                    <td>{c.messageCount.toLocaleString()}</td>
                    <td title={formatAbsolute(c.lastMessageAt)}>{formatRelative(c.lastMessageAt)}</td>
                    <td title={formatAbsolute(c.createdAt)}>{formatAbsolute(c.createdAt)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="pagination">
          <button
            className="btn btn-secondary"
            onClick={() => setPage(page - 1)}
            disabled={page === 1 || loading}
          >
            Previous
          </button>
          <span className="page-info">Page {page} of {totalPages}</span>
          <button
            className="btn btn-secondary"
            onClick={() => setPage(page + 1)}
            disabled={page >= totalPages || loading}
          >
            Next
          </button>
        </div>
      </div>

      <div style={{ marginTop: 12 }}>
        <Link className="link" to="/assistant/users">View users with chats →</Link>
      </div>
    </div>
  );
};

export default AssistantConversations;
